import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/ui/loading-button";
import type { AccountOrganizationPendingDeletion } from "@/contexts/accountContextState";
import { deleteAccount } from "@/lib/accountSettings";
import { showErrorToast } from "@/lib/toast";

import { FactorySettingsCard } from "./FactorySettingsCard";

export function DeleteAccountDangerZone({
  email,
  organizationsPendingDeletion = [],
}: {
  email: string;
  organizationsPendingDeletion?: AccountOrganizationPendingDeletion[];
}) {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const canDelete = confirmation.trim().toLowerCase() === email.toLowerCase();
  const marksOrganizations = organizationsPendingDeletion.length > 0;

  const handleDelete = async () => {
    if (!canDelete) {
      return;
    }
    setSaving(true);
    try {
      await deleteAccount(email);
      window.location.assign("/login");
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to delete account.");
      setSaving(false);
    }
  };

  return (
    <>
      <FactorySettingsCard title="Danger zone" data-testid="account-redesign-danger">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-0.5">
            <p className="text-[13px] font-medium text-foreground">Delete account</p>
            <p className="text-[12px] text-muted-foreground">
              {marksOrganizations
                ? "SuperPlane marks organizations where you are the last Owner. Those organizations stay for 30 days, then SuperPlane removes them. You lose access now."
                : "This deletes your SuperPlane account. You lose access now."}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => setOpen(true)}
            data-testid="account-redesign-delete"
          >
            Delete account
          </Button>
        </div>
      </FactorySettingsCard>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setConfirmation("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              Type {email} to confirm.
              {marksOrganizations
                ? " SuperPlane marks these organizations for deletion. Those organizations stay for 30 days, then SuperPlane removes them."
                : ""}
            </DialogDescription>
          </DialogHeader>
          {marksOrganizations ? (
            <ul className="list-disc space-y-1 pl-5 text-sm" data-testid="account-redesign-delete-orgs">
              {organizationsPendingDeletion.map((organization) => (
                <li key={organization.id}>{organization.name}</li>
              ))}
            </ul>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="account-delete-email">Email</Label>
            <Input
              id="account-delete-email"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              data-testid="account-redesign-delete-confirm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Keep account
            </Button>
            <LoadingButton
              variant="destructive"
              disabled={!canDelete}
              loading={saving}
              onClick={() => void handleDelete()}
              data-testid="account-redesign-delete-submit"
            >
              Delete account
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
